import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  AddCircle as AddCircleIcon
} from '@mui/icons-material';
import {
  getInventoryCountApprovalItems,
  approveInventoryCount,
  searchProductVariants,
  getMissingInventoryItems
} from '../../services/inventoryCountService';
import {
  InventoryCountRequest,
  InventoryCountApprovalItem,
  ApproveInventoryCountResponse
} from '../../types/inventoryCount';
import { formatDate } from '../../utils/dateUtils';

// نوع حالة الإشعار
interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

// نوع نتيجة البحث عن متغير المنتج
interface ProductVariantSearchResult {
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  unitId: string;
  unitName: string;
  currentQuantity: number;
}

const InventoryCountApproval: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [request, setRequest] = useState<InventoryCountRequest | null>(null);
  const [approvalItems, setApprovalItems] = useState<(InventoryCountApprovalItem & { manuallyAdded?: boolean })[]>([]);
  const [openAddItemDialog, setOpenAddItemDialog] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<ProductVariantSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantSearchResult | null>(null);
  const [newQuantity, setNewQuantity] = useState<number>(0);
  const [missingItems, setMissingItems] = useState<ProductVariantSearchResult[]>([]);
  const [missingItemsLoading, setMissingItemsLoading] = useState<boolean>(false);
  const [selectedMissingItems, setSelectedMissingItems] = useState<Set<string>>(new Set());
  const [showMissingItems, setShowMissingItems] = useState<boolean>(true);

  // جلب عناصر اعتماد الجرد
  useEffect(() => {
    const fetchApprovalItems = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const response = await getInventoryCountApprovalItems(id);
        if (response.success) {
          setRequest(response.request);
          setApprovalItems(response.approvalItems);
        } else {
          setError('حدث خطأ أثناء جلب بيانات الاعتماد');
        }
      } catch (err) {
        console.error('Error fetching inventory count approval items:', err);
        setError('حدث خطأ أثناء الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchApprovalItems();
  }, [id]);

  // جلب العناصر غير المدرجة في الجرد الحالي
  const fetchMissingItems = async () => {
    if (!id) return;
    
    setMissingItemsLoading(true);
    try {
      const response = await getMissingInventoryItems(id);
      if (response.success) {
        setMissingItems(response.missingItems);
      } else {
        console.error('Failed to fetch missing items');
      }
    } catch (err) {
      console.error('Error fetching missing inventory items:', err);
    } finally {
      setMissingItemsLoading(false);
    }
  };

  // جلب العناصر غير المدرجة عند فتح الحوار
  useEffect(() => {
    if (openAddItemDialog && showMissingItems) {
      fetchMissingItems();
    }
  }, [openAddItemDialog, showMissingItems, id]);

  // تغيير كمية عنصر
  const updateItemQuantity = (index: number, quantity: number) => {
    const updatedItems = [...approvalItems];
    updatedItems[index].newQuantity = quantity;
    updatedItems[index].difference = quantity - updatedItems[index].currentQuantity;
    setApprovalItems(updatedItems);
  };

  // حذف عنصر من قائمة الاعتماد
  const removeItem = (index: number) => {
    setApprovalItems(approvalItems.filter((_, i) => i !== index));
  };

  // البحث عن متغيرات المنتجات
  const handleSearch = async () => {
    if (!searchQuery) return;
    
    setSearchLoading(true);
    try {
      const response = await searchProductVariants(searchQuery);
      if (response.success) {
        // إضافة الكمية الحالية إلى نتائج البحث
        const resultsWithQuantity: ProductVariantSearchResult[] = response.variants.map(variant => ({
          ...variant,
          currentQuantity: variant.currentQuantity || 0
        }));
        setSearchResults(resultsWithQuantity);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching product variants:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // تنفيذ البحث عند الضغط على Enter
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // إضافة عنصر واحد إلى قائمة الاعتماد
  const handleAddItem = () => {
    if (!selectedVariant || newQuantity < 0) return;
    
    // التحقق مما إذا كان العنصر موجودًا بالفعل
    const existingIndex = approvalItems.findIndex(item =>
      item.productId === selectedVariant.productId && item.variantId === selectedVariant.variantId
    );
    
    if (existingIndex >= 0) {
      // تحديث العنصر الموجود
      const updatedItems = [...approvalItems];
      updatedItems[existingIndex].newQuantity = newQuantity;
      updatedItems[existingIndex].difference = newQuantity - updatedItems[existingIndex].currentQuantity;
      setApprovalItems(updatedItems);
    } else {
      // إضافة عنصر جديد
      const newItem: InventoryCountApprovalItem = {
        productId: selectedVariant.productId,
        productName: selectedVariant.productName,
        variantId: selectedVariant.variantId,
        variantAttributes: selectedVariant.variantAttributes,
        barcode: selectedVariant.barcode,
        currentQuantity: selectedVariant.currentQuantity,
        newQuantity: newQuantity,
        difference: newQuantity - selectedVariant.currentQuantity,
        unitId: selectedVariant.unitId,
        unitName: selectedVariant.unitName
      };
      setApprovalItems([...approvalItems, newItem]);
    }
    
    // إغلاق الحوار وإعادة تعيين القيم
    setOpenAddItemDialog(false);
    setSelectedVariant(null);
    setNewQuantity(0);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedMissingItems(new Set());
  };

  // تبديل اختيار العنصر المفقود
  const toggleMissingItemSelection = (itemKey: string) => {
    const newSelection = new Set(selectedMissingItems);
    if (newSelection.has(itemKey)) {
      newSelection.delete(itemKey);
    } else {
      newSelection.add(itemKey);
    }
    setSelectedMissingItems(newSelection);
  };

  // إضافة العناصر المختارة إلى قائمة الاعتماد
  const handleAddSelectedMissingItems = () => {
    if (selectedMissingItems.size === 0) return;

    const newItems: InventoryCountApprovalItem[] = [];
    const updatedItems = [...approvalItems];

    // تجهيز العناصر المختارة للإضافة
    missingItems.forEach(item => {
      const itemKey = `${item.productId}-${item.variantId}`;
      if (selectedMissingItems.has(itemKey)) {
        // التحقق مما إذا كان العنصر موجودًا بالفعل
        const existingIndex = approvalItems.findIndex(existingItem =>
          existingItem.productId === item.productId && existingItem.variantId === item.variantId
        );
        
        if (existingIndex >= 0) {
          // تحديث العنصر الموجود بكمية صفر
          updatedItems[existingIndex].newQuantity = 0;
          updatedItems[existingIndex].difference = -updatedItems[existingIndex].currentQuantity;
        } else {
          // إضافة عنصر جديد مع كمية جديدة صفر للعناصر الغير مدرجة
          newItems.push({
            productId: item.productId,
            productName: item.productName,
            variantId: item.variantId,
            variantAttributes: item.variantAttributes,
            barcode: item.barcode || '',
            currentQuantity: item.currentQuantity,
            // نحن بحاجة إلى السماح بتحديث الكمية للعناصر الغير مدرجة بناءً على الفرق
            newQuantity: 0, // نبدأ بصفر للعناصر الغير مدرجة ولكن يمكن تعديلها لاحقًا
            difference: -item.currentQuantity, // الفرق هو سالب الكمية الحالية
            unitId: item.unitId,
            unitName: item.unitName,
            manuallyAdded: true // تعليم العنصر كمضاف يدويًا
          });
        }
      }
    });

    // تحديث قائمة العناصر مع العناصر الجديدة
    setApprovalItems([...updatedItems, ...newItems]);
    
    // إغلاق الحوار وإعادة تعيين القيم
    setOpenAddItemDialog(false);
    setSelectedMissingItems(new Set());
  };

  // اعتماد الجرد
  const handleApproveInventory = async () => {
    if (!id || approvalItems.length === 0) return;
    
    setSubmitting(true);
    setError(null);
    try {
      console.log("العناصر قبل الإرسال:", approvalItems);
      
      // تحويل جميع العناصر إلى الصيغة المطلوبة للواجهة البرمجية مع الحفاظ على خاصية manuallyAdded
      const items = approvalItems.map(item => ({
        productId: item.productId,
        productName: item.productName,
        variantId: item.variantId,
        variantAttributes: item.variantAttributes,
        barcode: item.barcode || '',
        currentQuantity: item.currentQuantity,
        newQuantity: item.newQuantity,
        unitId: item.unitId,
        unitName: item.unitName,
        // إرسال manuallyAdded كقيمة boolean بشكل صريح وصحيح
        manuallyAdded: Boolean(item.manuallyAdded)
      }));
      
      // تسجيل قيم manuallyAdded للتأكد من صحتها
      console.log("العناصر غير المدرجة:", items.filter(i => i.manuallyAdded === true).length);
      console.log("تفاصيل العناصر غير المدرجة:", items.filter(i => i.manuallyAdded === true).map(item => ({
        productName: item.productName,
        currentQuantity: item.currentQuantity,
        newQuantity: item.newQuantity,
        manuallyAdded: item.manuallyAdded
      })));
      console.log("إجمالي العناصر للإرسال:", items.length);
      
      // إرسال الطلب مع جميع البيانات المطلوبة
      const response = await approveInventoryCount({
        requestId: id,
        items: items
      });
      
      if (response && response.success) {
        setSuccess(true);
        
        // التحقق من وجود إدخال جديد للعناصر غير المدرجة
        if (response.newEntry) {
          setSnackbar({
            open: true,
            message: `تم اعتماد طلب الجرد بنجاح وإنشاء إدخال جديد للعناصر غير المدرجة برقم ${response.newEntry.entryNumber}`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'تم اعتماد طلب الجرد بنجاح',
            severity: 'success'
          });
        }
        
        // الانتقال إلى صفحة تفاصيل الطلب بعد الاعتماد بنجاح
        setTimeout(() => {
          navigate(`/inventory-count/${id}`);
        }, 3000);
      } else {
        setError('حدث خطأ أثناء اعتماد الجرد');
      }
    } catch (err) {
      console.error('Error approving inventory count:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setSubmitting(false);
    }
  };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل بيانات الاعتماد...
        </Typography>
      </Box>
    );
  }

  // عرض رسالة الخطأ
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // عرض رسالة في حالة عدم وجود طلب
  if (!request) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        لم يتم العثور على طلب الجرد المطلوب
      </Alert>
    );
  }

  // التحقق مما إذا كان الطلب جاهزًا للاعتماد
  if (request.status !== 'completed') {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        لا يمكن اعتماد الجرد. الطلب في حالة {request.status}.
      </Alert>
    );
  }

  // عرض سمات المتغير كنص
  const formatVariantAttributes = (attributes?: Record<string, string>) => {
    if (!attributes) return '';
    
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  // الحصول على لون مؤشر الفرق بناءً على قيمة الفرق
  const getDifferenceColor = (difference: number) => {
    if (difference === 0) return 'success';
    if (difference > 0) return 'info';
    return 'error';
  };

  // الحصول على أيقونة مؤشر الفرق بناءً على قيمة الفرق
  const getDifferenceIcon = (difference: number) => {
    if (difference === 0) return <CheckCircleIcon />;
    if (difference > 0) return <AddIcon />;
    return <WarningAmberIcon />;
  };

  return (
    <Box>
      {/* رسالة النجاح */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          تم اعتماد الجرد بنجاح. جاري الانتقال إلى صفحة تفاصيل الطلب...
        </Alert>
      )}

      {/* معلومات الطلب */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h2">
            اعتماد جرد المستودع
          </Typography>
          <Chip
            label={`طلب رقم: ${request.requestNumber}`}
            color="primary"
            variant="outlined"
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              المستودع:
            </Typography>
            <Typography variant="body1">
              {request.warehouseName || 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              تاريخ الإنشاء:
            </Typography>
            <Typography variant="body1">
              {formatDate(request.createdAt)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* قائمة عناصر الاعتماد */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h3">
            عناصر الجرد
          </Typography>
          {/* تم إزالة زر إضافة العناصر غير المدرجة لأن إضافة العناصر غير المدرجة ستتم فقط من خلال أوامر الإدخال */}
        </Box>
        
        
        <Divider sx={{ mb: 2 }} />
        
        {/* تم إزالة التنبيه الخاص بالعناصر الغير مدرجة */}

        {approvalItems.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            لا توجد عناصر للاعتماد. قم بإضافة عناصر أو تأكد من وجود أوامر إدخال للطلب.
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 440, mb: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>اسم المنتج</TableCell>
                  <TableCell>متغير المنتج</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>الوحدة</TableCell>
                  <TableCell>الكمية الحالية</TableCell>
                  <TableCell>الكمية الجديدة</TableCell>
                  <TableCell>الفرق</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvalItems.map((item, index) => (
                  <TableRow key={`${item.productId}-${item.variantId}`} hover>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>
                      {formatVariantAttributes(item.variantAttributes) || '-'}
                    </TableCell>
                    <TableCell>{item.barcode || '-'}</TableCell>
                    <TableCell>{item.unitName}</TableCell>
                    <TableCell>{item.currentQuantity}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.newQuantity}
                        onChange={(e) => {
                          const value = Math.max(0, parseInt(e.target.value) || 0);
                          updateItemQuantity(index, value);
                        }}
                        size="small"
                        InputProps={{
                          inputProps: { min: 0 },
                          // إضافة نص توضيحي للعناصر الغير مدرجة
                          startAdornment: item.manuallyAdded ? (
                            <Tooltip title="هذا عنصر غير مدرج (سيتم تحديث المخزون بناءً على الفرق)">
                              <span style={{ fontSize: '10px', color: 'purple', marginRight: '4px' }}>غير مدرج</span>
                            </Tooltip>
                          ) : null
                        }}
                        sx={{
                          width: item.manuallyAdded ? '120px' : '80px',
                          '& input': {
                            color: item.manuallyAdded ? 'purple' : 'inherit'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={item.manuallyAdded ? "secondary" : getDifferenceColor(item.difference) as any}
                        icon={getDifferenceIcon(item.difference)}
                        label={item.difference > 0 ? `+${item.difference}` : item.difference}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => removeItem(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {/* صف الإجمالي */}
                <TableRow sx={{
                  backgroundColor: 'grey.100',
                  '& .MuiTableCell-root': {
                    fontWeight: 'bold',
                    borderTop: '2px solid',
                    borderColor: 'primary.main'
                  }
                }}>
                  <TableCell>الإجمالي</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>
                    {approvalItems.reduce((total, item) => total + item.currentQuantity, 0)}
                  </TableCell>
                  <TableCell sx={{ color: 'success.main' }}>
                    {approvalItems.reduce((total, item) => total + item.newQuantity, 0)}
                  </TableCell>
                  <TableCell sx={{
                    color: (() => {
                      const totalDifference = approvalItems.reduce((total, item) => total + item.difference, 0);
                      return totalDifference > 0 ? 'success.main' : totalDifference < 0 ? 'error.main' : 'text.primary';
                    })()
                  }}>
                    {(() => {
                      const totalDifference = approvalItems.reduce((total, item) => total + item.difference, 0);
                      return totalDifference > 0 ? `+${totalDifference}` : totalDifference;
                    })()}
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ملخص الفروقات */}
        {approvalItems.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`مطابق: ${approvalItems.filter(item => item.difference === 0).length}`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<AddIcon />}
              label={`زيادة: ${approvalItems.filter(item => item.difference > 0).length}`}
              color="info"
              variant="outlined"
            />
            <Chip
              icon={<WarningAmberIcon />}
              label={`نقص: ${approvalItems.filter(item => item.difference < 0).length}`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<AddCircleIcon />}
              label={`غير مدرج: ${approvalItems.filter(item => item.manuallyAdded).length}`}
              color="secondary"
              variant="outlined"
            />
          </Box>
        )}
      </Paper>

      {/* أزرار الإجراءات */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory-count/${id}`)}
        >
          رجوع
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleApproveInventory}
          disabled={submitting || approvalItems.length === 0}
        >
          {submitting ? 'جاري الاعتماد...' : 'اعتماد الجرد'}
        </Button>
      </Box>

      {/* حوار إضافة العناصر غير المدرجة */}
      <Dialog
        open={openAddItemDialog}
        onClose={() => setOpenAddItemDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {showMissingItems
            ? "إضافة العناصر غير المدرجة في الجرد"
            : "إضافة عنصر جديد"}
        </DialogTitle>
        <DialogContent>
          {showMissingItems ? (
            <Box sx={{ mb: 2, mt: 1 }}>
              {missingItemsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              ) : missingItems.length > 0 ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    هذه العناصر لها كمية في المستودع (موجبة أو سالبة) ولم يتم إدراجها في عمليات الجرد الحالية.
                    سيتم إعطاؤها قيمة جديدة "صفر" عند إضافتها.
                  </Alert>
                  <TableContainer sx={{ maxHeight: 400, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" align="center">اختيار</TableCell>
                          <TableCell>اسم المنتج</TableCell>
                          <TableCell>متغير المنتج</TableCell>
                          <TableCell>الباركود</TableCell>
                          <TableCell>الوحدة</TableCell>
                          <TableCell>الكمية الحالية</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {missingItems.map((item) => {
                          const itemKey = `${item.productId}-${item.variantId}`;
                          return (
                            <TableRow
                              key={itemKey}
                              hover
                              selected={selectedMissingItems.has(itemKey)}
                              onClick={() => toggleMissingItemSelection(itemKey)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox" align="center">
                                <Chip
                                  label={selectedMissingItems.has(itemKey) ? "✓" : ""}
                                  variant={selectedMissingItems.has(itemKey) ? "filled" : "outlined"}
                                  color={selectedMissingItems.has(itemKey) ? "primary" : "default"}
                                  size="small"
                                  sx={{ minWidth: '30px' }}
                                />
                              </TableCell>
                              <TableCell>{item.productName}</TableCell>
                              <TableCell>
                                {formatVariantAttributes(item.variantAttributes) || '-'}
                              </TableCell>
                              <TableCell>{item.barcode || '-'}</TableCell>
                              <TableCell>{item.unitName}</TableCell>
                              <TableCell>{item.currentQuantity}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                      onClick={() => {
                        // اختيار كل العناصر
                        const allKeys = new Set(missingItems.map(item => `${item.productId}-${item.variantId}`));
                        setSelectedMissingItems(allKeys);
                      }}
                      color="primary"
                    >
                      اختيار الكل
                    </Button>
                    <Button
                      onClick={() => setSelectedMissingItems(new Set())}
                      color="error"
                    >
                      إلغاء الاختيار
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  لا توجد عناصر إضافية للجرد. جميع العناصر ذات الكمية غير الصفرية مدرجة بالفعل.
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 2, mt: 1 }}>
              <TextField
                autoFocus
                label="ابحث عن اسم المنتج، الباركود، أو أي من خصائص المنتج"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} disabled={searchLoading}>
                        {searchLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {searchResults.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    نتائج البحث:
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>اسم المنتج</TableCell>
                          <TableCell>متغير المنتج</TableCell>
                          <TableCell>الباركود</TableCell>
                          <TableCell>الوحدة</TableCell>
                          <TableCell>الكمية الحالية</TableCell>
                          <TableCell>الإجراءات</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map((variant) => (
                          <TableRow
                            key={variant.variantId}
                            hover
                            selected={selectedVariant?.variantId === variant.variantId}
                          >
                            <TableCell>{variant.productName}</TableCell>
                            <TableCell>
                              {formatVariantAttributes(variant.variantAttributes) || '-'}
                            </TableCell>
                            <TableCell>{variant.barcode || '-'}</TableCell>
                            <TableCell>{variant.unitName}</TableCell>
                            <TableCell>{variant.currentQuantity}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                color={selectedVariant?.variantId === variant.variantId ? 'success' : 'primary'}
                                onClick={() => setSelectedVariant(variant)}
                              >
                                {selectedVariant?.variantId === variant.variantId ? 'تم الاختيار' : 'اختيار'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {selectedVariant && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        أدخل الكمية الجديدة:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          label="الكمية الجديدة"
                          type="number"
                          fullWidth
                          value={newQuantity}
                          onChange={(e) => setNewQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                          InputProps={{
                            inputProps: { min: 0 },
                            endAdornment: <InputAdornment position="end">{selectedVariant.unitName}</InputAdornment>
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : searchQuery && !searchLoading ? (
                <Alert severity="info">لم يتم العثور على نتائج</Alert>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowMissingItems(!showMissingItems)}
            color="secondary"
          >
            {showMissingItems ? "تبديل إلى البحث اليدوي" : "تبديل إلى العناصر غير المدرجة"}
          </Button>
          <Button onClick={() => setOpenAddItemDialog(false)}>إلغاء</Button>
          {showMissingItems ? (
            <Button
              onClick={handleAddSelectedMissingItems}
              variant="contained"
              disabled={selectedMissingItems.size === 0}
              color="primary"
            >
              إضافة العناصر المختارة ({selectedMissingItems.size})
            </Button>
          ) : (
            <Button
              onClick={handleAddItem}
              variant="contained"
              disabled={!selectedVariant || newQuantity < 0}
            >
              إضافة
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* إشعارات التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryCountApproval;